import { useMemo } from 'react';
import { useAuth } from 'src/context/auth/AuthContext';
import { resolveViewScope } from 'src/config/permissions';
import { ViewScope } from 'src/types/permissions.types';

export interface ViewScopeResult {
  scope: ViewScope;
  isSelfScoped: boolean;
  canViewAll: boolean;
  /** Owner to filter by under `ASSIGNED`; undefined for `ALL` and `SELF`. */
  ownerFilterId?: string;
}

/**
 * Resolves the read scope of the current user for one resource.
 * Example: const { isSelfScoped, ownerFilterId } = useViewScope('transactions');
 */
export function useViewScope(resource: string): ViewScopeResult {
  const { userRole, user, rolesVersion } = useAuth();

  return useMemo(() => {
    const scope = resolveViewScope(userRole, resource);
    const ownerFilterId = scope === 'ASSIGNED' ? user?.id || user?.email : undefined;
    return {
      scope,
      isSelfScoped: scope === 'SELF',
      canViewAll: scope === 'ALL',
      ownerFilterId,
    };
    // rolesVersion: permissions can change at runtime from the Roles page.
  }, [userRole, resource, user?.id, user?.email, rolesVersion]);
}

export default useViewScope;
